// The one rule behind the Vehicle Health verdict and the Diagnostic Codes tab bubble,
// so the two cannot disagree. Dates are formatted by the caller through formatDate.

import {parseApiDate} from "@/composables/useTimezone";

const DAY_MS = 86400000;

// Readings older than this cannot back a verdict
export const FRESH_DAYS = 7;
export const DATE_ONLY = {hour: undefined, minute: undefined, second: undefined};
// API times without a zone are UTC. A date over a day ahead is a bad device clock, not a fresh report.
export const isFresh = (at) => {
    const time = parseApiDate(at)?.getTime();
    if (!time) return false;
    const age = Date.now() - time;
    return age <= FRESH_DAYS * DAY_MS && age >= -DAY_MS;
};

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;
const sentence = (causes) => causes.join(', ').replace(/^./, c => c.toUpperCase());

// The firmware sends lamp state only on a change or after power loss, so an old report still
// stands: age never means off, it only keeps a lit lamp from counting as urgent.
// j1939: raw DM1 values (0 off, 1 on, 2 error, 3 not available), null on OBD-II.
// checkEngine: the check engine flag, null with no lamp record. On J1939 it can be on with every
// DM1 lamp off, and it is then the only sign. timed: false on older API builds that send no time.
export const readLamps = ({j1939 = null, checkEngine = null, at = null, timed = true}) => {
    const fresh = !timed || isFresh(at);
    const mil = checkEngine === null || checkEngine === undefined ? null : !!checkEngine;
    const values = j1939 ? [j1939.red_stop_lamp, j1939.amber_warning_lamp, j1939.protect_lamp] : [];
    const dm1Lit = values.filter(v => v === 1).length;
    const errors = values.filter(v => v === 2).length;
    return {
        at,
        fresh,
        isJ1939: !!j1939,
        redLit: values[0] === 1,
        amberLit: values[1] === 1,
        protectLit: values[2] === 1,
        // Only the check engine flag is lit, on either kind of vehicle
        milOnly: !dm1Lit && mil === true,
        lit: dm1Lit || (mil ? 1 : 0),
        errors,
        allOff: j1939
            ? mil !== true && !dm1Lit && !errors && values.includes(0) && values.every(v => v === 0 || v === 3)
            : mil === false,
        redStopOn: fresh && values[0] === 1,
        // The lamp that makes a stored code active
        engineLampOn: fresh && (j1939 ? values[0] === 1 : !!mil),
    };
};

// One row per code. Red only for a stored code with the lamp on; a stored code with the lamp off,
// a permanent code (already cleared, kept until the car re-tests) and a pending code are amber.
export const listFaultCodes = (groups, engineLampOn) => {
    const rank = {stored: 3, permanent: 2, pending: 1};
    const byCode = new Map();
    Object.entries(groups ?? {}).forEach(([type, codes]) => {
        const kind = /pending/i.test(type) ? 'pending' : (/permanent/i.test(type) ? 'permanent' : 'stored');
        (codes ?? []).forEach((dtc) => {
            const seen = byCode.get(dtc.code);
            if (seen && rank[seen.kind] >= rank[kind]) return;
            byCode.set(dtc.code, {...dtc, kind});
        });
    });
    const labels = {stored: 'Confirmed', permanent: 'Permanent', pending: 'Pending'};
    return [...byCode.values()]
        .map(dtc => ({
            ...dtc,
            active: dtc.kind === 'stored' && engineLampOn,
            color: dtc.kind === 'stored' && engineLampOn ? 'error' : 'warning',
            typeLabel: labels[dtc.kind],
        }))
        .sort((a, b) => rank[b.kind] - rank[a.kind]);
};

// What the lamps and fault codes alone say. Codes older than FRESH_DAYS are listed but never colour
// anything. Codes that did not load but are known to exist are amber, never red.
// codeStatus: ok, none, loading, denied or error; reportedCount: codes known without the list.
export const assessDiagnostics = ({lamps, codeStatus, groups = null, codesAt = null, reportedCount = 0, formatDate}) => {
    const codes = listFaultCodes(codeStatus === 'ok' ? groups : null, lamps.engineLampOn);
    const codesFresh = codeStatus === 'ok' && isFresh(codesAt);
    const active = codesFresh ? codes.filter(d => d.active).length : 0;
    const other = codesFresh ? codes.length - active : 0;
    const lastReported = lamps.at ? ` (last reported ${formatDate(lamps.at)})` : '';

    const red = [];
    const amber = [];
    if (lamps.lit && !lamps.fresh) {
        const what = lamps.milOnly || !lamps.isJ1939
            ? 'check engine light'
            : (lamps.lit === 1 ? 'warning lamp' : `${lamps.lit} warning lamps`);
        amber.push(`${what} on${lastReported}`);
    } else if (lamps.milOnly) {
        (active ? red : amber).push('check engine light on');
    } else {
        if (lamps.redLit) red.push('red stop lamp on');
        if (lamps.amberLit) amber.push('amber warning lamp on');
        if (lamps.protectLit) amber.push('protect lamp on');
    }
    if (lamps.errors) {
        amber.push(`${lamps.errors === 1 ? 'warning lamp' : `${lamps.errors} warning lamps`} reporting an error${lamps.fresh ? '' : lastReported}`);
    }
    const lampCauses = [...red, ...amber];

    if (active) red.unshift(plural(active, 'active fault code'));
    if (other) amber.push(plural(other, 'fault code'));
    const knownCount = Number(reportedCount) || 0;
    if (codeStatus !== 'ok' && codeStatus !== 'none' && knownCount > 0) {
        amber.push(plural(knownCount, 'fault code'));
    }
    return {codes, codesFresh, red, amber, lampCauses};
};

// Whether the lamps and codes leave the header's All OK tick standing: every answer in, nothing
// amber or red. Old codes colour nothing, so they do not stop it, as on the tab and the bubble.
export const diagnosticsClear = ({diagnostics, codeStatus}) => ['ok', 'none', 'denied', 'error'].includes(codeStatus)
    && !diagnostics.red.length && !diagnostics.amber.length;

// The banner on the Vehicle Health tab. readingCauses: out of range readings, counted only while
// the engine data is fresh. Findings show whatever the age or absence of the engine data.
export const vehicleVerdict = ({lamps, diagnostics, paramsAsOf, readingCauses = [], formatDate}) => {
    const engineFresh = isFresh(paramsAsOf);
    const red = diagnostics.red;
    const amber = [...diagnostics.amber, ...(engineFresh ? readingCauses : [])];
    const detail = sentence([...red, ...amber]);
    if (red.length) return {color: 'error', icon: 'mdi-alert-octagon', title: 'Needs attention now', detail};
    if (amber.length) return {color: 'warning', icon: 'mdi-alert', title: 'Check soon', detail};
    if (!paramsAsOf) return null;
    if (!engineFresh) {
        return {
            color: 'grey', icon: 'mdi-help-circle-outline',
            title: `Last engine data ${formatDate(paramsAsOf)}, status unknown`, detail: '',
        };
    }
    const codesText = diagnostics.codesFresh ? 'No fault codes' : 'No recent fault code report';
    return {
        color: 'success', icon: 'mdi-check-circle', title: 'All systems normal',
        detail: lamps.allOff
            ? `${codesText}, no warning lamps, and every reading is in its normal range.`
            : `${codesText}, and every reading is in its normal range. Warning lamps have not been reported.`,
    };
};

// The Diagnostic Codes tab bubble, or null: nothing until the codes have answered, and nothing for
// a role refused them. Its colour is the verdict's on the same findings.
export const diagnosticBubble = ({diagnostics, lamps, codeStatus, reportedCount = 0}) => {
    if (!['ok', 'none', 'error'].includes(codeStatus)) return null;
    const known = codeStatus === 'ok' ? diagnostics.codes.length : (codeStatus === 'error' ? Number(reportedCount) || 0 : 0);
    const count = known || lamps.lit || lamps.errors;
    if (!count) return null;
    const severity = diagnostics.red.length ? 'error' : (diagnostics.amber.length ? 'warning' : 'neutral');
    const parts = [
        ...(known ? [known === 1 ? '1 diagnostic code' : `${known} diagnostic codes`] : []),
        ...(known && !diagnostics.red.length && !diagnostics.amber.length ? [`last reported over ${FRESH_DAYS} days ago`] : []),
        ...diagnostics.lampCauses,
    ];
    return {count, severity, label: sentence(parts)};
};
